import React from 'react';
import {
    Box,
    Divider,
    Flex,
    Icon,
    IconButton,
    Link,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from '@chakra-ui/react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { formatDateWithTimezone, getInvoiceStatusTranslation } from 'ts/common/common';
import { DownloadIcon, PrinterIcon } from 'ts/common/components/icons';
import { useTranslate } from 'ts/common/hooks';
import { printPdf } from 'ts/common/utils';
import ResponsiveButtonGroup from 'client_react/common/components/ResponsiveButtonGroup';

const InvoiceLandingPage: React.FC = () => {
    const t = useTranslate('invoice');
    const { invoice } = useOutletContext<{ invoice: SpApi.Client.IInvoice }>();
    const navigate = useNavigate();

    const openPaymentPage = () => {
        navigate(`/invoice/${invoice.publicId}/payment`);
    };

    const downloadInvoice = () => {
        open(invoice.links.alternate.href, '_blank');
    };

    const printInvoice = () => {
        return printPdf(invoice.links.alternate.href);
    };

    return (
        <Box maxW="1000px" mx="auto" as="main">
            <Flex justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Text fontSize="20px" fontWeight="600" as="h1">
                    {t('headingText', {
                        customInvoiceId: invoice.customInvoiceId
                    })}
                </Text>
                <Flex gap={4} alignItems="center">
                    <IconButton
                        icon={<Icon as={PrinterIcon} boxSize="24px" />}
                        variant="ghost"
                        size="24px"
                        aria-label={t('print')}
                        onClick={printInvoice}
                        cursor="pointer"
                    />
                    <Link onClick={downloadInvoice} download _hover={{ textDecoration: 'none' }}>
                        <IconButton
                            icon={<Icon as={DownloadIcon} boxSize="24px" />}
                            variant="ghost"
                            size="24px"
                            aria-label={t('download')}
                            cursor="pointer"
                        />
                    </Link>
                </Flex>
            </Flex>
            <Box
                bg="transparent"
                px={3}
                py={1}
                border="1px solid"
                borderRadius="full"
                display="inline-block"
                fontSize="12px"
                fontFamily="open-sans"
                mb={3}
                textTransform="uppercase"
            >
                {getInvoiceStatusTranslation(t, invoice.invoiceStatus)}
            </Box>

            {/* Invoice Info Section */}
            <Flex
                mb={10}
                gap="30px"
                data-testid="invoice-info-section"
                alignItems="flex-start"
                role="region"
            >
                <Flex
                    gap="30px"
                    flexWrap="wrap"
                    flex="1 1 0%"
                    as="dl"
                    aria-label={t('invoiceDetails')}
                >
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('from')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {invoice.brandName}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('to')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {invoice.contact.name}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('issued')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {formatDateWithTimezone(invoice.created)}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('amountDue')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {`${invoice.currencySymbol}${invoice.dueTotal}`}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('dueDate')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {formatDateWithTimezone(invoice.finalDueDate)}
                        </Text>
                    </Box>
                    {invoice.contracts.length > 0 && (
                        <Box
                            flex="0 1 auto"
                            minW="fit-content"
                            display="flex"
                            flexDirection="column"
                        >
                            <Text fontSize="14px" fontWeight="600" mb={1}>
                                {t('linkedDocuments')}
                            </Text>
                            {invoice.contracts.map((contract) => {
                                return (
                                    <Link
                                        key={contract.publicId}
                                        href={contract.links.client.href}
                                        target="_blank"
                                        _hover={{ textDecoration: 'none' }}
                                    >
                                        <Text
                                            fontSize="14px"
                                            fontWeight="400"
                                            color="brand.primary"
                                        >
                                            {contract.name}
                                        </Text>
                                    </Link>
                                );
                            })}
                        </Box>
                    )}
                </Flex>
            </Flex>

            <Box textAlign="center" width="100%">
                <ResponsiveButtonGroup
                    buttons={[
                        {
                            onClick: openPaymentPage,
                            text: t('payInvoice'),
                            variant: 'primary'
                        }
                    ]}
                />
            </Box>

            <Box mb={12} mt={12} role="region" aria-label={t('invoiceContent')}>
                <Text as="h2" fontSize="20px" fontWeight="400" mb={8} textTransform="none">
                    {t('items')}
                </Text>
                <Table>
                    <Thead>
                        <Tr>
                            <Th>{t('item')}</Th>
                            <Th>{t('itemPrice')}</Th>
                            <Th>{t('quantity')}</Th>
                            <Th>{t('totalPrice')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {[...invoice.items]
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((item, index) => {
                                return (
                                    <Tr key={index}>
                                        <Td>
                                            <Flex flexDirection="column" gap={2}>
                                                <Text fontWeight="600">{item.name}</Text>
                                                {item.description && (
                                                    <Text>{item.description}</Text>
                                                )}
                                            </Flex>
                                        </Td>
                                        <Td>{item.itemPrice}</Td>
                                        <Td>{item.quantity}</Td>
                                        <Td>{item.totalPrice}</Td>
                                    </Tr>
                                );
                            })}
                        {invoice.discounts.map((discount, index) => {
                            return (
                                <Tr key={index}>
                                    <Td>
                                        <Flex flexDirection="column" gap={2}>
                                            <Text fontWeight="600">{discount.name}</Text>
                                            {discount.description && (
                                                <Text>{discount.description}</Text>
                                            )}
                                        </Flex>
                                    </Td>
                                    <Td textTransform="uppercase">
                                        {discount.discountType === 'fixed'
                                            ? t('discountOff', {
                                                  value: `${invoice.currencySymbol}${discount.value}`
                                              })
                                            : `${discount.value}%`}
                                    </Td>
                                    <Td>-</Td>
                                    <Td>
                                        -{invoice.currencySymbol}
                                        {invoice.discountTotal}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>

            {/* Invoice Notes and Financial Breakdown Section */}
            <Flex
                mb={12}
                gap="60px"
                data-testid="invoice-notes-breakdown-section"
                alignItems="flex-start"
                role="region"
                aria-label={t('invoiceSummary')}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
                {/* Invoice Notes */}
                {invoice.notesToClient && (
                    <Box flex="1" minW="300px">
                        <Text as="h3" fontSize="18px" fontWeight="600" mb={4}>
                            {t('notes')}
                        </Text>
                        <Text fontSize="14px" lineHeight="1.6" whiteSpace="pre-wrap">
                            {invoice.notesToClient}
                        </Text>
                    </Box>
                )}

                {/* Financial Breakdown */}
                <Box flex="0 0 auto" minW="300px">
                    <Box borderRadius="md" p={6} bg="gray.50">
                        <Flex justify="space-between" mb={2}>
                            <Text>{t('subtotal')}</Text>
                            <Text>
                                {invoice.currencySymbol}
                                {invoice.subtotal}
                            </Text>
                        </Flex>
                        {invoice.discountTotal > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>{t('discounts')}</Text>
                                <Text>
                                    -{invoice.currencySymbol}
                                    {invoice.discountTotal}
                                </Text>
                            </Flex>
                        )}
                        {invoice.salesTaxPercent > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>
                                    {t('salesTax')} ({invoice.salesTaxPercent}%)
                                </Text>
                                <Text>
                                    {invoice.currencySymbol}
                                    {invoice.salesTaxTotal}
                                </Text>
                            </Flex>
                        )}
                        <Divider borderColor="gray.300" />
                        <Flex justify="space-between" mb={2}>
                            <Text>{t('grandTotal')}</Text>
                            <Text>
                                {invoice.currencySymbol}
                                {invoice.grandTotal}
                            </Text>
                        </Flex>
                        {invoice.paidTotal > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>{t('totalPaid')}</Text>
                                <Text>
                                    {invoice.currencySymbol}
                                    {invoice.paidTotal}
                                </Text>
                            </Flex>
                        )}
                        <Divider borderColor="gray.300" />
                        <Flex justify="space-between">
                            <Text fontSize="20px" fontWeight="600">
                                {t('balanceDue')}
                            </Text>
                            <Text fontSize="16px" fontWeight="600" color="red.600">
                                {invoice.currencySymbol}
                                {invoice.dueTotal}
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Flex>

            {/* Invoice Installments Section */}
            {invoice.installments.length > 0 && (
                <Box mb={12} role="region" aria-label={t('installments')}>
                    <Text as="h2" fontSize="20px" fontWeight="400" mb={8} textTransform="none">
                        {t('installments')}
                    </Text>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>{t('installmentNumber')}</Th>
                                <Th>{t('due')}</Th>
                                <Th>{t('amount')}</Th>
                                <Th>{t('status')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {invoice.installments.map((installment, index) => {
                                const isOverdue = new Date(installment.dueDate) < new Date();
                                const statusColor =
                                    installment.invoiceInstallmentStatus === 'paid-in-full'
                                        ? 'green.600'
                                        : installment.invoiceInstallmentStatus === 'partially-paid'
                                        ? 'orange.600'
                                        : isOverdue
                                        ? 'red.600'
                                        : 'gray.600';

                                return (
                                    <Tr key={index}>
                                        <Td fontWeight="500">
                                            {installment.isRetainer
                                                ? t('retainer')
                                                : t('installment')}{' '}
                                            #{index + 1}
                                        </Td>
                                        <Td>
                                            <Text>
                                                {formatDateWithTimezone(installment.dueDate)}
                                            </Text>
                                            {isOverdue &&
                                                installment.invoiceInstallmentStatus !==
                                                    'paid-in-full' && (
                                                    <Text
                                                        fontSize="12px"
                                                        color="red.600"
                                                        fontWeight="500"
                                                    >
                                                        {t('overdue')}
                                                    </Text>
                                                )}
                                        </Td>
                                        <Td fontWeight="500">
                                            {invoice.currencySymbol}
                                            {installment.amount}
                                        </Td>
                                        <Td>
                                            <Text
                                                color={statusColor}
                                                fontWeight="500"
                                                textTransform="capitalize"
                                            >
                                                {t(
                                                    `installmentStatus.${installment.invoiceInstallmentStatus}`
                                                )}
                                            </Text>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </Box>
    );
};

export default InvoiceLandingPage;
